/**
 * Support → Request Board (`/request-board`) — super admins only (the gate is
 * this screen's; the backend is open to any logged-in user).
 *
 * A kanban board for the requests that used to arrive as replies to the
 * deployment notes in Teams. Cards are filed into the two request columns,
 * worked top to bottom (bugs first), parked in Requires Backend Changes with a
 * note of what the backend needs, and sent for review, where they are approved
 * or sent back with a comment. There are no roles: anyone here can do anything.
 *
 * Data: the real `/request_board` API through useRequestBoard (cards, moves,
 * comments, images, backend details, history), with live updates over the
 * notifications socket and a polling fallback.
 *
 * The board is hand-built rather than AG Grid on purpose: it is a set of
 * drag-and-drop card lanes, not a table of records.
 */
import { Children, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import { alpha } from "@mui/material/styles";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import InputBase from "@mui/material/InputBase";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import AddRounded from "@mui/icons-material/AddRounded";
import DoneAllRounded from "@mui/icons-material/DoneAllRounded";
import KeyRounded from "@mui/icons-material/KeyRounded";
import ApartmentRounded from "@mui/icons-material/ApartmentRounded";
import InboxOutlined from "@mui/icons-material/InboxOutlined";
import BoltRounded from "@mui/icons-material/BoltRounded";
import ExpandMoreRounded from "@mui/icons-material/ExpandMoreRounded";
import OpenInNewRounded from "@mui/icons-material/OpenInNewRounded";
import NotificationsNoneOutlined from "@mui/icons-material/NotificationsNoneOutlined";
import RefreshRounded from "@mui/icons-material/RefreshRounded";
import ScheduleRounded from "@mui/icons-material/ScheduleRounded";
import { useSnackbar } from "notistack";
import CodeRounded from "@mui/icons-material/CodeRounded";

import Flx from "../../components/layout/Flx";
import Txt from "../../components/typography/Txt";
import ConfirmationDialog from "../../components/ui/ConfirmationDialog";
import AuthTokenDialog from "./AuthTokenDialog";
import QuickEditDialog from "../../components/forms/QuickEditDialog";
import {
  FormFieldGrid,
  FormField,
  FormLinkTargetField,
  FormTextField,
} from "../../components/forms/FormPrimitives";
import { FORM, formSx, px } from "../../theme/formStyles";
import { usePermissionsHook } from "../../hooks/usePermissionsHook";
import { neutralButton, slate, status, teal } from "../../theme/colors";
import { AdminScreenShell } from "../admin/shared/AdminScreenShell";
import { A } from "../admin/shared/adminTokens";
import {
  COL,
  COLUMNS,
  APP_SCREENS,
  contextArea,
  sameContext,
  tabsFor,
  PLATFORMS,
  platformShort,
  ZONES,
  NEEDS,
  URGENCY,
  requestsOrder,
  canAddTo,
  BULK_MOVES,
  columnByKey,
  hitSummary,
} from "./requestBoardModel";
import {
  BoardCard,
  ImageThumbs,
  ImageViewer,
  filesToImages,
  pastedImageFiles,
} from "./requestBoardParts";
import RequestBoardItemDrawer from "./RequestBoardItemDrawer";
import { HandoffDialog } from "./requestBoardHandoff";
import { AlertsDialog, HistoryPanel } from "./RequestBoardDialogs";
import { DEFAULT_PREFS } from "./requestBoardActivity";
import useRequestBoard, { normalizeEvent } from "./useRequestBoard";
import { requestBoardApi, requestBoardErrorMessage } from "../../api/requestBoardApi";


/**
 * A title-sized page tab (the page's own title doubles as its view switcher):
 * active is ink with a brand-teal underline and a teal count badge; inactive is
 * muted slate with a gray badge.
 */
const TitleTab = ({ active, label, count, onClick }) => (
  <Box
    component="button"
    type="button"
    role="tab"
    aria-selected={active}
    onClick={onClick}
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 1,
      p: 0,
      pb: 0.75,
      border: 0,
      borderBottom: `3px solid ${active ? teal[500] : "transparent"}`,
      bgcolor: "transparent",
      font: "inherit",
      fontSize: 22,
      fontWeight: 700,
      letterSpacing: "-.015em",
      lineHeight: 1.25,
      color: active ? A.ink : slate[400],
      cursor: active ? "default" : "pointer",
      transition: "color .15s, border-color .15s",
      "&:hover": { color: active ? A.ink : slate[500] },
      "&:focus-visible": { outline: `2px solid ${teal[300]}`, outlineOffset: 2, borderRadius: "4px" },
    }}
  >
    {label}
    {Number.isFinite(count) ? (
      <Box
        component="span"
        sx={{
          minWidth: 24,
          px: 0.75,
          borderRadius: 99,
          textAlign: "center",
          fontSize: 12,
          fontWeight: 700,
          lineHeight: "22px",
          letterSpacing: 0,
          // White on teal needs teal-600 (the action shade) for contrast.
          bgcolor: active ? teal[600] : slate[100],
          color: active ? "#FFFFFF" : slate[500],
        }}
      >
        {count}
      </Box>
    ) : null}
  </Box>
);

TitleTab.propTypes = {
  active: PropTypes.bool,
  label: PropTypes.string.isRequired,
  count: PropTypes.number,
  onClick: PropTypes.func.isRequired,
};



const sortByRank = (list) => [...list].sort((a, b) => a.rank - b.rank);

/* ──────────────────────────────── Column ──────────────────────────────── */

/**
 * How far (px) the "between cards" slot reaches into each neighbouring card
 * when dragging into another lane. Inside it the drop line shows; anywhere
 * else in the lane (on a card, the header, empty space) the to-top overlay does.
 */
const SLOT_REACH_PX = 14;

/**
 * The card a drop between cards would land before (`null` = after the last
 * card), or `undefined` when the pointer isn't in a between-cards slot.
 */
const slotAt = (body, y, excludeId) => {
  const cards = [...body.querySelectorAll("[data-card-id]")].filter(
    (el) => el.dataset.cardId !== excludeId,
  );
  // Only the visible card area counts — not the lane header, nor cards
  // scrolled out of view.
  const area = body.getBoundingClientRect();
  if (!cards.length || y < area.top || y > area.bottom) return undefined;
  const rects = cards.map((el) => el.getBoundingClientRect());
  for (let i = 0; i <= cards.length; i += 1) {
    const above = rects[i - 1];
    const below = rects[i];
    // The open ends (before the first / after the last card) get a slot as
    // tall as a gap between two cards plus the reach on each side.
    const top = above ? above.bottom - SLOT_REACH_PX : below.top - 2 * SLOT_REACH_PX;
    const bottom = below ? below.top + SLOT_REACH_PX : above.bottom + 2 * SLOT_REACH_PX;
    if (y >= top && y <= bottom) return below ? cards[i].dataset.cardId : null;
  }
  return undefined;
};

/**
 * The development zone's "gray on teal" recipe — values as specified for this
 * board (2026-10-08): a teal panel with near-white lanes on it.
 */
const DEV_ZONE = {
  bg: "#D8EFF4",
  border: "#A9DBE5",
  laneBg: "#F7F9FB",
  laneBorder: "#C6E3EA",
  laneShadow: "0 1px 2px rgba(16,60,72,.06)",
};

/**
 * The page header's buttons (Refresh, Alerts, New request) share one size:
 * 36px tall, 8px corners, 14px side padding — so the icon button and the two
 * labelled buttons line up.
 */
const HEADER_BTN = { height: 36, minHeight: 36, py: 0, px: "14px", borderRadius: "8px" };

/** Narrowest a lane gets; past that the board scrolls sideways. */
const LANE_MIN_W = 320;

/**
 * Each zone is as wide as its lanes (Requests has one, the others two): a
 * minimum of LANE_MIN_W per lane plus the zone's padding and gaps, and a share
 * of the spare width in proportion to its lane count.
 */
const ZONE_TEMPLATE = ZONES.map((z) => {
  const cols = COLUMNS.filter((c) => c.zone === z.key);
  const n = cols.length || 1;
  const min = cols.reduce((sum, c) => sum + (c.minWidth || LANE_MIN_W), 0) || LANE_MIN_W;
  return `minmax(${min + 24 + (n - 1) * 12}px, ${n}fr)`;
}).join(" ");

/**
 * Covers a lane while a card from another lane hovers anywhere but a slot
 * between cards: releasing adds the card to the top. Pointer events pass
 * through so the lane keeps receiving the drag.
 */
const DropToTopOverlay = () => (
  <Flx
    column
    center
    g={0.5}
    sx={{
      position: "absolute",
      inset: 0,
      zIndex: 2,
      pointerEvents: "none",
      borderRadius: "12px",
      bgcolor: alpha(teal[100], 0.88),
      boxShadow: `inset 0 0 0 2px ${teal[400]}`,
      textAlign: "center",
      px: 3,
    }}
  >
    <Txt component="div" sx={{ fontSize: 14, fontWeight: 700, color: teal[700] }}>
      Release to place at the top
    </Txt>
    <Txt component="div" sx={{ fontSize: 12.5, color: A.ink2 }}>
      or drag into a gap between cards to place it there
    </Txt>
  </Flx>
);

/** A keyboard key cap for the quick-add hints. */
const Kbd = ({ children }) => (
  <Box
    component="kbd"
    sx={{
      px: "5px",
      py: "1px",
      border: `1px solid ${A.line}`,
      borderBottomWidth: 2,
      borderRadius: "4px",
      bgcolor: A.surface,
      fontFamily: "inherit",
      fontSize: 10.5,
      fontWeight: 600,
      color: A.ink2,
      lineHeight: 1.4,
    }}
  >
    {children}
  </Box>
);

Kbd.propTypes = { children: PropTypes.node };

/** An outlined chip that toggles (the quick add's tags and Urgent). */
const QuickChip = ({ on, tone, onClick, icon, children }) => (
  <Box
    component="button"
    type="button"
    aria-pressed={on}
    onClick={onClick}
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.5,
      px: 1.25,
      py: "3px",
      borderRadius: "7px",
      border: `1px solid ${on ? tone?.fg || A.teal : A.lineStrong}`,
      bgcolor: on ? tone?.bg || A.tealSoft : A.surface,
      color: on ? tone?.fg || A.tealDeep : A.ink2,
      font: "inherit",
      fontSize: 12.5,
      fontWeight: 500,
      cursor: "pointer",
      "& svg": { fontSize: 14 },
      "&:hover": { borderColor: on ? tone?.fg || A.teal : A.muted },
      "&:focus-visible": { outline: `2px solid ${A.tealSoft2}`, outlineOffset: 1 },
    }}
  >
    {icon}
    {children}
  </Box>
);

QuickChip.propTypes = {
  on: PropTypes.bool,
  tone: PropTypes.object,
  onClick: PropTypes.func.isRequired,
  icon: PropTypes.node,
  children: PropTypes.node,
};

/** A borderless text button in the quick add's footer. */
const QuickLink = ({ onClick, icon, children, menu }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    aria-haspopup={menu ? "menu" : undefined}
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.5,
      px: 0.75,
      py: 0.5,
      border: 0,
      borderRadius: "6px",
      bgcolor: "transparent",
      font: "inherit",
      fontSize: 12.5,
      fontWeight: 500,
      color: A.ink2,
      cursor: "pointer",
      whiteSpace: "nowrap",
      flexShrink: 0,
      "& svg": { fontSize: 15, color: A.muted },
      "&:hover": { bgcolor: slate[100] },
    }}
  >
    {icon}
    {children}
  </Box>
);

QuickLink.propTypes = {
  onClick: PropTypes.func.isRequired,
  icon: PropTypes.node,
  children: PropTypes.node,
  menu: PropTypes.bool,
};

/**
 * The inline quick-add card, at the top of the Requests lane: a borderless
 * title ("What do you need?"), a details line, optional tags and Urgent, then
 * a footer with the company, "Full form" (hands the draft to the New request
 * form), Cancel and a split "Add to bottom / top" button. Keys: Enter adds,
 * Shift+Enter jumps to the details, Esc closes. Screenshots paste anywhere.
 */
const QuickAddCard = ({ onAdd, onCancel, onMore, onOpenImages }) => {
  const [needs, setNeeds] = useState([]);
  const [urgent, setUrgent] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [platform, setPlatform] = useState("both");
  const [position, setPosition] = useState("bottom");
  // Screenshots pasted while the card is open (Ctrl/Cmd+V in any of its fields).
  const [images, setImages] = useState([]);
  const [companyAnchor, setCompanyAnchor] = useState(null);
  const [positionAnchor, setPositionAnchor] = useState(null);
  const detailsRef = useRef(null);

  const draft = {
    needs,
    urgency: urgent ? "urgent" : "normal",
    title,
    description,
    platform,
    position,
    images,
  };
  const submit = () => title.trim() && onAdd(draft);
  const where = urgent ? "Urgent" : position === "top" ? "top" : "bottom";
  const keys = (e) => {
    if (e.key === "Escape") onCancel();
  };

  return (
    <Box sx={{ flexShrink: 0 }}>
      <Box
        onPaste={(e) => {
          const imgs = filesToImages(pastedImageFiles(e));
          if (!imgs.length) return;
          e.preventDefault();
          setImages((prev) => [...prev, ...imgs]);
        }}
        sx={{
          bgcolor: A.surface,
          border: `1.5px solid ${teal[500]}`,
          borderRadius: "10px",
          boxShadow: `0 0 0 3px ${alpha(teal[500], 0.15)}`,
          overflow: "hidden",
        }}
      >
        <Box sx={{ px: 1.5, pt: 1.25, pb: 1 }}>
          <InputBase
            autoFocus
            fullWidth
            placeholder="What do you need?"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => {
              keys(e);
              if (e.key === "Enter" && e.shiftKey) {
                e.preventDefault();
                detailsRef.current?.focus();
              } else if (e.key === "Enter") {
                e.preventDefault();
                submit();
              }
            }}
            inputProps={{ "aria-label": "Request" }}
            sx={{ fontSize: 14.5, fontWeight: 500, color: A.ink, "& input": { p: 0 } }}
          />
          <InputBase
            fullWidth
            multiline
            maxRows={6}
            inputRef={detailsRef}
            placeholder="Details (optional) — paste screenshots anywhere"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            onKeyDown={(e) => {
              keys(e);
              if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                submit();
              }
            }}
            inputProps={{ "aria-label": "Details" }}
            sx={{ mt: 0.5, fontSize: 12.5, color: A.ink2, "& textarea": { p: 0 } }}
          />
          {images.length ? (
            <Box sx={{ mt: 1 }}>
              <ImageThumbs
                images={images}
                size={52}
                onOpen={onOpenImages}
                onRemove={(id) => setImages((prev) => prev.filter((i) => i.id !== id))}
              />
            </Box>
          ) : null}
          <Flx ac jb g={0.75} wrap sx={{ mt: 1.25 }}>
            <Flx ac g={0.75} wrap>
              {NEEDS.map((n) => {
                const on = needs.includes(n.key);
                return (
                  <QuickChip
                    key={n.key}
                    on={on}
                    tone={n}
                    onClick={() =>
                      setNeeds((prev) => (on ? prev.filter((k) => k !== n.key) : [...prev, n.key]))
                    }
                  >
                    {n.label}
                  </QuickChip>
                );
              })}
            </Flx>
            <QuickChip
              on={urgent}
              tone={{ bg: status.error.bg, fg: status.error.fg }}
              icon={<BoltRounded />}
              onClick={() => setUrgent((v) => !v)}
            >
              Urgent
            </QuickChip>
          </Flx>
        </Box>

        {/* footer — company, full form, cancel, split add button */}
        <Flx ac jb g={0.5} sx={{ px: 1, py: 0.75, borderTop: `1px solid ${A.line}`, flexWrap: "nowrap" }}>
          <Flx ac g={0.25}>
            <QuickLink
              menu
              icon={<ApartmentRounded />}
              onClick={(e) => setCompanyAnchor(e.currentTarget)}
            >
              {platformShort(platform)}
              <ExpandMoreRounded sx={{ fontSize: "15px !important" }} />
            </QuickLink>
            <QuickLink icon={<OpenInNewRounded />} onClick={() => onMore(draft)}>
              Full form
            </QuickLink>
          </Flx>
          <Flx ac g={0.5}>
            <QuickLink onClick={onCancel}>Cancel</QuickLink>
            <Flx sx={{ "& > button": { boxShadow: "none" } }}>
              <Button
                size="small"
                variant="contained"
                disabled={!title.trim()}
                onClick={submit}
                sx={{ borderRadius: "7px 0 0 7px", px: 1.5, whiteSpace: "nowrap", flexShrink: 0 }}
              >
                {urgent ? "Add to Urgent" : `Add to ${where}`}
              </Button>
              <Button
                size="small"
                variant="contained"
                aria-label="Choose where to add it"
                onClick={(e) => setPositionAnchor(e.currentTarget)}
                sx={{
                  minWidth: 0,
                  px: 0.5,
                  borderRadius: "0 7px 7px 0",
                  borderLeft: `1px solid ${alpha("#FFFFFF", 0.35)}`,
                }}
              >
                <ExpandMoreRounded sx={{ fontSize: 18 }} />
              </Button>
            </Flx>
          </Flx>
        </Flx>
      </Box>

      {/* keyboard hints */}
      <Flx ac g={1.25} sx={{ justifyContent: "flex-end", mt: 0.75, mr: 0.5, color: A.faint, fontSize: 11.5 }}>
        <Flx ac g={0.5}>
          <Kbd>↵</Kbd> add
        </Flx>
        <Flx ac g={0.5}>
          <Kbd>Shift</Kbd>
          <Kbd>↵</Kbd> details
        </Flx>
        <Flx ac g={0.5}>
          <Kbd>Esc</Kbd> close
        </Flx>
      </Flx>

      <Menu anchorEl={companyAnchor} open={!!companyAnchor} onClose={() => setCompanyAnchor(null)}>
        {PLATFORMS.map((p) => (
          <MenuItem
            key={p.value}
            selected={p.value === platform}
            onClick={() => {
              setPlatform(p.value);
              setCompanyAnchor(null);
            }}
            sx={{ fontSize: 13 }}
          >
            {p.short} ({p.label})
          </MenuItem>
        ))}
      </Menu>
      <Menu
        anchorEl={positionAnchor}
        open={!!positionAnchor}
        onClose={() => setPositionAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {[
          { value: "top", label: urgent ? "Add to top of Urgent" : "Add to top of queue" },
          { value: "bottom", label: urgent ? "Add to bottom of Urgent" : "Add to bottom of queue" },
        ].map((o) => (
          <MenuItem
            key={o.value}
            selected={o.value === position}
            onClick={() => {
              setPosition(o.value);
              setPositionAnchor(null);
            }}
            sx={{ fontSize: 13 }}
          >
            {o.label}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

QuickAddCard.propTypes = {
  onAdd: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onMore: PropTypes.func.isRequired,
  onOpenImages: PropTypes.func.isRequired,
};

/** A section label inside a lane ("Urgent 2", "Queue 5"). */
/**
 * A section label inside a lane — uppercase "URGENT · HANDLED FIRST" /
 * "QUEUE · RANKED", an optional dot, and a hairline running to the lane edge.
 */
const SectionHead = ({ label, color, dot, lineColor, sx }) => (
  <Flx ac g={0.75} sx={sx}>
    {dot ? <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: color, flexShrink: 0 }} /> : null}
    <Txt
      sx={{
        fontSize: 11.5,
        fontWeight: 700,
        letterSpacing: ".08em",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
        color,
      }}
    >
      {label}
    </Txt>
    <Box sx={{ flex: 1, height: "1px", bgcolor: lineColor || A.line }} />
  </Flx>
);

SectionHead.propTypes = {
  label: PropTypes.string.isRequired,
  color: PropTypes.string,
  dot: PropTypes.bool,
  lineColor: PropTypes.string,
  sx: PropTypes.object,
};

/** The Requests lane with nothing in it: what it's for, and two ways to add. */
const EmptyRequests = ({ onQuickAdd, onFullForm }) => (
  <Flx
    column
    ac
    g={1.25}
    sx={{
      px: 3,
      py: 3,
      textAlign: "center",
      border: `1px dashed ${A.lineStrong}`,
      borderRadius: "10px",
      bgcolor: A.surface,
    }}
  >
    <Flx
      center
      sx={{
        width: 40,
        height: 40,
        borderRadius: "50%",
        bgcolor: A.tealSoft,
        color: A.tealDeep,
        "& svg": { fontSize: 22 },
      }}
    >
      <InboxOutlined />
    </Flx>
    <Flx column g={0.5}>
      <Txt component="div" sx={{ fontSize: 14, fontWeight: 600, color: A.ink }}>
        No requests yet
      </Txt>
      <Txt component="div" sx={{ fontSize: 12.5, lineHeight: 1.5, color: A.muted, maxWidth: 280 }}>
        File a fix, a change or a question. It joins the queue, and urgent ones are pinned on top.
      </Txt>
    </Flx>
    <Flx ac g={1} sx={{ mt: 0.5 }}>
      <Button size="small" variant="contained" startIcon={<AddRounded />} onClick={onQuickAdd} sx={{ borderRadius: "8px" }}>
        Add a request
      </Button>
      <Button size="small" variant="text" onClick={onFullForm}>
        Full form
      </Button>
    </Flx>
  </Flx>
);

EmptyRequests.propTypes = {
  onQuickAdd: PropTypes.func.isRequired,
  onFullForm: PropTypes.func.isRequired,
};

const EmptyLane = ({ children }) => (
  <Txt
    component="div"
    sx={{
      mt: 0.5,
      py: 3,
      textAlign: "center",
      fontSize: 12.5,
      color: A.faint,
      border: `1px dashed ${A.lineStrong}`,
      borderRadius: "10px",
    }}
  >
    {children}
  </Txt>
);

EmptyLane.propTypes = { children: PropTypes.node };

const DropLine = () => (
  <Box sx={{ height: 3, mx: 0.5, borderRadius: 2, bgcolor: teal[500] }} />
);

const BoardColumn = ({
  col,
  tinted,
  items,
  drag,
  dropTarget,
  onAdd,
  onQuickAdd,
  onOpenImages,
  onOpen,
  openId,
  nextUpId,
  onDragStart,
  onDragEnd,
  onDragOverColumn,
  onDropColumn,
  me,
  onHit,
  onBulk,
}) => {
  const bodyRef = useRef(null);
  const bulk = BULK_MOVES[col.key];
  // Requests: Urgent cards pinned in their own section above the ranked Queue.
  const sectioned = col.key === COL.REQUESTS;
  const urgentItems = sectioned ? items.filter((i) => i.urgency === "urgent") : [];
  const queueItems = sectioned ? items.filter((i) => i.urgency !== "urgent") : items;
  const [quickOpen, setQuickOpen] = useState(false);
  // Any card can be dragged into any lane.
  const dragging = !!drag;
  const isTarget = dropTarget?.col === col.key;
  // "top": the card just arrived — the whole lane is the target and a release
  // adds it to the top. "precise": after the hold, place it between cards.
  const toTop = isTarget && dropTarget.mode === "top";
  const precise = isTarget && dropTarget.mode === "precise";

  /** Cards with the drop line where a release would land (per section in Requests). */
  const renderCards = (list, section, ranked) =>
    list.map((item, i) => (
      <Box key={item.id} sx={{ display: "contents" }}>
        {precise && dropTarget.section === section && dropTarget.beforeId === item.id ? <DropLine /> : null}
        <BoardCard
          item={item}
          rank={ranked ? i + 1 : undefined}
          nextUp={item.id === nextUpId}
          draggable
          dragging={drag?.id === item.id}
          selected={openId === item.id}
          onOpen={onOpen}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          me={me}
          onHit={onHit}
        />
      </Box>
    ));

  return (
    <Flx
      column
      onDragOver={(e) => dragging && onDragOverColumn(e, col.key, bodyRef.current)}
      onDrop={(e) => dragging && onDropColumn(e, col.key)}
      sx={{
        minWidth: 0,
        minHeight: 0,
        height: "100%",
        position: "relative",
        // Every lane shares the development lanes' face (#F7F9FB + soft shadow).
        bgcolor: DEV_ZONE.laneBg,
        border: `1px solid ${isTarget ? teal[300] : tinted ? DEV_ZONE.laneBorder : A.line}`,
        boxShadow: DEV_ZONE.laneShadow,
        borderRadius: "12px",
        transition: "border-color .15s",
      }}
    >
      {toTop ? <DropToTopOverlay /> : null}
      <Box sx={{ px: 1.75, pt: 1.5, pb: 1.25, flexShrink: 0 }}>
        <Flx ac jb g={1}>
          <Flx ac g={1}>
            <Box sx={{ width: 9, height: 9, borderRadius: "50%", bgcolor: col.dot }} />
            <Txt sx={{ fontSize: 14, fontWeight: 700, color: A.ink }}>{col.label}</Txt>
            <Txt sx={{ fontSize: 13, color: A.faint, fontWeight: 600 }}>
              {items.length}
            </Txt>
          </Flx>
          {canAddTo(col.key) ? (
            <Tooltip title="Quick add">
              <IconButton
                size="small"
                onClick={() => {
                  setQuickOpen(true);
                  bodyRef.current?.scrollTo({ top: 0, behavior: "smooth" });
                }}
                sx={{ color: A.muted, borderRadius: "7px" }}
              >
                <AddRounded sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          ) : null}
          {/* Complete All / Approve All: the whole lane on to the next stage. */}
          {bulk && items.length && onBulk ? (
            <Button
              size="small"
              variant="contained"
              disableElevation
              startIcon={<DoneAllRounded sx={{ fontSize: "16px !important" }} />}
              onClick={() => onBulk(col.key)}
              sx={{
                height: 28,
                minHeight: 28,
                py: 0,
                px: "10px",
                borderRadius: "7px",
                fontSize: 12,
                fontWeight: 600,
                whiteSpace: "nowrap",
              }}
            >
              {bulk.label}
            </Button>
          ) : null}
        </Flx>
        <Flx ac g={1} sx={{ mt: 1 }}>
          <Txt sx={{ fontSize: 12, color: A.muted, lineHeight: 1.35 }}>{col.blurb}</Txt>
        </Flx>
      </Box>

      <Box
        ref={bodyRef}
        sx={{
          flex: 1,
          minHeight: 0,
          // Each lane scrolls on its own; the board runs to the bottom of the screen.
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 1.25,
          px: 1.25,
          pt: 0.25,
          pb: 1.5,
        }}
      >
        {quickOpen ? (
          <QuickAddCard
            colKey={col.key}
            onOpenImages={onOpenImages}
            onCancel={() => setQuickOpen(false)}
            onAdd={(draft) => {
              onQuickAdd(col.key, draft);
              setQuickOpen(false);
            }}
            onMore={(draft) => {
              setQuickOpen(false);
              onAdd(col.key, draft);
            }}
          />
        ) : null}
        {sectioned && !items.length && !dragging && !quickOpen ? (
          /* No requests at all: an invitation to file the first one. */
          <EmptyRequests
            onQuickAdd={() => setQuickOpen(true)}
            onFullForm={() => onAdd(col.key)}
          />
        ) : sectioned ? (
          <>
            {/* Urgent — pinned, worked first, not numbered. Dropping a card
                here marks it urgent. Shown only when something is urgent, or
                while a card is being dragged (so it can be dropped in). */}
            {urgentItems.length || dragging ? (
            <Box
              data-section="urgent"
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1.25,
                px: 1.25,
                pt: 1.25,
                pb: urgentItems.length ? 1.25 : 0,
                borderRadius: "10px",
                bgcolor: alpha(status.error.bg, 0.6),
                border: `1.5px dashed ${alpha(status.error.fg, 0.35)}`,
              }}
            >
              <SectionHead
                label="Urgent · Handled first"
                color={status.error.fg}
                dot
                lineColor={alpha(status.error.fg, 0.2)}
              />
              {renderCards(urgentItems, "urgent", false)}
              {precise && dropTarget.section === "urgent" && dropTarget.beforeId == null ? <DropLine /> : null}
              {!urgentItems.length ? (
                <Txt
                  component="div"
                  sx={{ py: 2, textAlign: "center", fontSize: 12.5, color: alpha(status.error.fg, 0.8) }}
                >
                  Drag here if it can&apos;t wait
                </Txt>
              ) : null}
            </Box>
            ) : null}
            <SectionHead label="Queue · Ranked" color={A.faint} sx={{ mt: 0.5 }} />
            <Box data-section="queue" sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
              {renderCards(queueItems, "queue", true)}
              {precise && dropTarget.section === "queue" && dropTarget.beforeId == null ? <DropLine /> : null}
              {!queueItems.length && !isTarget ? <EmptyLane>The queue is empty</EmptyLane> : null}
            </Box>
          </>
        ) : (
          <>
            {renderCards(items, undefined, !!col.ranked)}
            {precise && dropTarget.beforeId == null ? <DropLine /> : null}
            {!items.length && !isTarget ? <EmptyLane>Nothing here</EmptyLane> : null}
          </>
        )}
      </Box>
    </Flx>
  );
};

BoardColumn.propTypes = {
  col: PropTypes.object.isRequired,
  tinted: PropTypes.bool,
  items: PropTypes.array.isRequired,
  drag: PropTypes.object,
  dropTarget: PropTypes.object,
  onAdd: PropTypes.func.isRequired,
  onQuickAdd: PropTypes.func.isRequired,
  onOpenImages: PropTypes.func.isRequired,
  onOpen: PropTypes.func.isRequired,
  onDragStart: PropTypes.func.isRequired,
  onDragEnd: PropTypes.func.isRequired,
  onDragOverColumn: PropTypes.func.isRequired,
  onDropColumn: PropTypes.func.isRequired,
  openId: PropTypes.string,
  nextUpId: PropTypes.string,
};

/* ───────────────────────────────── Zone ───────────────────────────────── */

/** Only the development zone carries an icon tile; Requests and Review are label-only. */
const ZONE_ICONS = {
  development: CodeRounded,
};

/**
 * A labelled group of lanes — icon tile (development only) and uppercase label. Every zone keeps the same padding so
 * the lanes line up; only the tinted one draws its panel.
 */
const BoardZone = ({ zone, children }) => {
  const Icon = ZONE_ICONS[zone.key];
  return (
    <Flx
      column
      sx={{
        minWidth: 0,
        minHeight: 0,
        p: 0.75,
        borderRadius: "14px",
        border: `1.5px solid ${zone.tinted ? DEV_ZONE.border : "transparent"}`,
        bgcolor: zone.tinted ? DEV_ZONE.bg : "transparent",
      }}
    >
      {/* minHeight keeps the row the icon tile's height so lanes line up
          across zones with and without one. */}
      <Flx ac g={1.25} wrap sx={{ flexShrink: 0, px: 0.5, pb: 0.75, minHeight: 26 }}>
        {Icon ? (
          <Flx
            center
            sx={{
              width: 26,
              height: 26,
              borderRadius: "7px",
              bgcolor: zone.tinted ? teal[500] : A.surface,
              border: `1px solid ${zone.tinted ? teal[500] : A.line}`,
              color: zone.tinted ? "#FFFFFF" : A.ink2,
              "& svg": { fontSize: 16 },
            }}
          >
            <Icon />
          </Flx>
        ) : null}
        <Txt
          sx={{
            fontSize: 13,
            fontWeight: 700,
            letterSpacing: ".08em",
            textTransform: "uppercase",
            color: zone.tinted ? teal[700] : A.ink2,
          }}
        >
          {zone.label}
        </Txt>
        {zone.blurb ? (
          <Txt sx={{ fontSize: 13, color: A.muted }}>· {zone.blurb}</Txt>
        ) : null}
      </Flx>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "grid",
          gridTemplateColumns: `repeat(${Children.count(children)}, minmax(0, 1fr))`,
          gap: 1.5,
        }}
      >
        {children}
      </Box>
    </Flx>
  );
};

BoardZone.propTypes = {
  zone: PropTypes.object.isRequired,
  children: PropTypes.node,
};

/** A field label with a quiet "optional" (and an optional hint) after it. */
const OptionalLabel = ({ children, hint }) => (
  <>
    {children}
    <Box component="span" sx={{ ml: 0.75, fontWeight: 400, color: A.faint }}>
      optional{hint ? ` · ${hint}` : ""}
    </Box>
  </>
);

OptionalLabel.propTypes = { children: PropTypes.node, hint: PropTypes.string };

/** The New request form's need cards: a checkbox, the tag and what it means. */
const NEED_CARDS = [
  { key: "fix", label: "Fix", sub: "Something's broken" },
  { key: "change", label: "Change", sub: "Add or adjust" },
  { key: "question", label: "Question", sub: "Need an answer" },
];

const NeedCard = ({ need, on, onToggle }) => (
  <Box
    component="label"
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1,
      px: 1.25,
      py: 1,
      borderRadius: "8px",
      border: `1px solid ${on ? A.teal : A.lineStrong}`,
      bgcolor: on ? A.tealSoft : A.surface,
      cursor: "pointer",
      transition: "border-color .12s, background-color .12s",
      "&:hover": { borderColor: on ? A.teal : A.muted },
      "&:focus-within": { boxShadow: `0 0 0 3px ${A.tealSoft}` },
    }}
  >
    <Checkbox size="small" checked={on} onChange={onToggle} sx={{ p: 0 }} />
    <Box sx={{ minWidth: 0 }}>
      <Txt component="div" sx={{ fontSize: 12, fontWeight: 600, color: A.ink, lineHeight: 1.3 }}>
        {need.label}
      </Txt>
      <Txt component="div" sx={{ fontSize: 11, color: A.muted, lineHeight: 1.3 }}>
        {need.sub}
      </Txt>
    </Box>
  </Box>
);

NeedCard.propTypes = {
  need: PropTypes.object.isRequired,
  on: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
};

/** A small select for the form's bottom row (label comes from its FormField). */
const FormSelect = ({ label, value, options, onChange, icon }) => (
  <TextField
    select
    size="small"
    fullWidth
    value={value}
    onChange={(e) => onChange(e.target.value)}
    inputProps={{ "aria-label": label }}
    InputProps={icon ? { startAdornment: <InputAdornment position="start">{icon}</InputAdornment> } : undefined}
    sx={formSx.input}
  >
    {options.map((o) => (
      <MenuItem key={o.value} value={o.value}>
        {o.label}
      </MenuItem>
    ))}
  </TextField>
);

FormSelect.propTypes = {
  label: PropTypes.string,
  value: PropTypes.string,
  options: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  icon: PropTypes.node,
};

/* ──────────────────────────────── Screen ──────────────────────────────── */

const EMPTY_DRAFT = {
  needs: [],
  urgency: "normal",
  title: "",
  description: "",
  platform: "both",
  context: null,
  position: "bottom",
  images: [],
};

/**
 * The forms' "Images" field — the same thumbnail row the card panel shows:
 * thumbnails (with ×), then a dashed add tile the size of a thumbnail. Dropping
 * images on the row adds them too, and Ctrl/Cmd+V anywhere in the form pastes
 * screenshots (each form wires its own paste listener).
 */
const ImagesField = ({ images, onChange, onOpenImages, helperText }) => {
  const picker = useRef(null);
  const add = (files) => {
    const imgs = filesToImages(files);
    if (imgs.length) onChange([...(images || []), ...imgs]);
  };
  return (
    <FormField span={6} label="Images" helperText={helperText}>
      <Box
        onDragOver={(e) => {
          if ([...(e.dataTransfer?.types || [])].includes("Files")) e.preventDefault();
        }}
        onDrop={(e) => {
          e.preventDefault();
          add(e.dataTransfer?.files);
        }}
      >
        <ImageThumbs
          images={images || []}
          onOpen={onOpenImages}
          onRemove={(id) => onChange((images || []).filter((i) => i.id !== id))}
          onAdd={() => picker.current?.click()}
          addLabel="Add images, or paste a screenshot (Ctrl+V)"
        />
        <input
          ref={picker}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={(e) => {
            add(e.target.files);
            e.target.value = "";
          }}
        />
      </Box>
    </FormField>
  );
};

ImagesField.propTypes = {
  images: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  onOpenImages: PropTypes.func.isRequired,
  helperText: PropTypes.node,
};

/** Compares a field before / after an edit (the loan by its id). */
const sameValue = (k, a, b) => {
  if (k === "context") return sameContext(a, b);
  if (k === "needs") return JSON.stringify(a || []) === JSON.stringify(b || []);
  return (a || "") === (b || "");
};

export default function RequestBoardScreen() {
  const { isSuperAdmin, canSeeRequestBoardToken } = usePermissionsHook();
  // The auth-token modal (board developers only).
  const [tokenOpen, setTokenOpen] = useState(false);
  const location = useLocation();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const board = useRequestBoard();
  const { items, me, changeTick, lastChangeRef } = board;

  // A copied item link (`?item=<id>`, also what board alerts link to) opens
  // that card's panel — on load and whenever the link changes.
  const [openId, setOpenId] = useState(() => new URLSearchParams(location.search).get("item") || null);
  useEffect(() => {
    const linked = new URLSearchParams(location.search).get("item");
    if (linked) setOpenId(linked);
  }, [location.search]);
  // A linked card that isn't on the board (an older Completed one, or a
  // REQ number) is fetched on its own.
  const fetchItem = board.fetchItem;
  useEffect(() => {
    if (!openId || board.loading || items.some((i) => i.id === openId)) return;
    fetchItem(openId).then((it) => {
      if (it && it.id !== openId) setOpenId(it.id);
    });
  }, [openId, board.loading, items, fetchItem]);

  const [drag, setDrag] = useState(null);
  const [dropTarget, setDropTargetState] = useState(null);
  // dragover fires faster than renders commit; handlers read the latest target
  // from this ref so a stale render can't trigger redundant updates.
  const dropRef = useRef(null);
  const setDropTarget = useCallback((next) => {
    dropRef.current = typeof next === "function" ? next(dropRef.current) : next;
    setDropTargetState(dropRef.current);
  }, []);
  // Send back needs words first: the reason, posted as a comment.
  const [pending, setPending] = useState(null);
  const [newDraft, setNewDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  // Complete All / Approve All waiting on its confirm: the lane's key, or null.
  const [bulkFrom, setBulkFrom] = useState(null);
  // The image viewer: { images, index } or null.
  const [viewer, setViewer] = useState(null);
  // Board | History tab.
  const [view, setView] = useState("board");
  const [alertsOpen, setAlertsOpen] = useState(false);
  const [alertPrefs, setAlertPrefs] = useState(DEFAULT_PREFS);
  useEffect(() => {
    if (!alertsOpen) return;
    requestBoardApi
      .getAlertPrefs()
      .then(setAlertPrefs)
      .catch((err) =>
        enqueueSnackbar(requestBoardErrorMessage(err, "Alert settings couldn't be loaded."), {
          variant: "error",
        }),
      );
  }, [alertsOpen, enqueueSnackbar]);

  // Every lane in board order (Requests: Urgent first, then rank). Unfiltered —
  // the panel's "n of N" and drop positions use these.
  const byColumn = useMemo(() => {
    const out = {};
    COLUMNS.forEach((c) => {
      const lane = items.filter((i) => i.column === c.key);
      out[c.key] = c.key === COL.REQUESTS ? [...lane].sort(requestsOrder) : sortByRank(lane);
    });
    return out;
  }, [items]);

  // "Next up": the first Urgent request, or the Queue's #1.
  const nextUpId = byColumn[COL.REQUESTS]?.[0]?.id || null;

  const openItem = items.find((i) => i.id === openId) || null;

  /* history — the open card's timeline, and the board-wide History tab */

  const [itemHistory, setItemHistory] = useState([]);
  // Refetch the open card's history when THAT card changes, not on every other card's.
  // (A board-wide refresh notes a change with no card, which refetches it too.)
  const lastChangedId = lastChangeRef.current?.itemId;
  const openItemTick = lastChangedId === openId || lastChangedId == null ? changeTick : 0;
  const [boardHistory, setBoardHistory] = useState([]);
  useEffect(() => {
    if (!openId) {
      setItemHistory([]);
      return undefined;
    }
    let live = true;
    requestBoardApi
      .getActivity({ itemId: openId, limit: 200 })
      .then((res) => live && setItemHistory((res.events || []).map(normalizeEvent)))
      .catch(() => live && setItemHistory([]));
    return () => {
      live = false;
    };
  }, [openId, openItemTick]);

  useEffect(() => {
    if (view !== "history") return undefined;
    let live = true;
    requestBoardApi
      .getActivity({ limit: 200 })
      .then((res) => live && setBoardHistory((res.events || []).map(normalizeEvent)))
      .catch((err) => {
        if (live) {
          enqueueSnackbar(requestBoardErrorMessage(err, "History couldn't be loaded."), {
            variant: "error",
          });
        }
      });
    return () => {
      live = false;
    };
  }, [view, changeTick, enqueueSnackbar]);

  const openImages = useCallback((images, index) => setViewer({ images, index }), []);

  /** Every move goes through here so the "needs words first" rules can't be skipped. */
  const requestMove = useCallback(
    (id, to, beforeId = null, urgency = undefined) => {
      const item = items.find((i) => i.id === id);
      if (!item) return;
      if (to === COL.REQUESTS && item.column !== COL.REVIEW) {
        board.moveItem(id, to, beforeId, null, { urgency: urgency || item.urgency || "normal" });
        return;
      }
      if (to !== item.column && to === COL.BACKEND) {
        // The card doesn't move until the backend details are filled in.
        setHandoff({ id, beforeId, edit: false });
      } else if (item.column === COL.REVIEW && to === COL.PROGRESS) {
        setPending({ id, to, beforeId, kind: "sendBack", text: "", images: [] });
      } else {
        board.moveItem(id, to, beforeId);
      }
    },
    [items, board],
  );

  // Ctrl/Cmd+V while the Send back dialog is open adds the pasted screenshots
  // to it (a pasted text still goes into the text box).
  const pendingOpen = !!pending;
  useEffect(() => {
    if (!pendingOpen) return undefined;
    const onPaste = (e) => {
      const files = pastedImageFiles(e);
      if (!files.length) return;
      e.preventDefault();
      setPending((prev) =>
        prev
          ? {
              ...prev,
              images: [
                ...(prev.images || []),
                ...filesToImages(files),
              ],
            }
          : prev,
      );
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [pendingOpen]);

  const confirmPending = async () => {
    const { id, to, beforeId, text, images } = pending;
    setSaving(true);
    const ok = await board.moveItem(id, to, beforeId, { body: text.trim(), images: images || [] });
    setSaving(false);
    if (ok) setPending(null);
  };

  /* backend details (moving into Requires Backend Changes) */

  const [handoff, setHandoff] = useState(null); // { id, beforeId, edit }
  const handoffItem = handoff ? items.find((i) => i.id === handoff.id) : null;

  const submitHandoff = async (blocker) => {
    const { id, beforeId, edit } = handoff;
    setHandoff(null);
    if (edit) {
      if (await board.editBackendDetails(id, blocker)) {
        enqueueSnackbar("Backend details updated", { variant: "success" });
      }
      return;
    }
    const n = blocker.items.length;
    if (await board.addBackendDetails(id, beforeId, blocker)) {
      enqueueSnackbar(`Moved to Requires Backend Changes · ${n} task${n === 1 ? "" : "s"}`, {
        variant: "success",
      });
    }
  };

  /** Tick / untick one backend task on the card. */
  /** Recurring Encounters: log one, then offer Undo in case of a mis-click. */
  const addHit = board.addHit;
  const removeHit = board.removeHit;
  const hit = useCallback(
    async (id) => {
      const res = await addHit(id);
      if (!res?.item) return;
      const mine = hitSummary(res.item, me).mine;
      const hitId = res.hit?.id;
      enqueueSnackbar(`Encounter logged. You've run into this ${mine} time${mine === 1 ? "" : "s"}.`, {
        variant: "success",
        action: hitId
          ? (key) => (
              <Button
                size="small"
                onClick={() => {
                  closeSnackbar(key);
                  removeHit(id, hitId);
                }}
                sx={{
                  color: "#FFFFFF",
                  bgcolor: "rgba(255,255,255,.16)",
                  fontWeight: 600,
                  "&:hover": { bgcolor: "rgba(255,255,255,.28)" },
                }}
              >
                Undo
              </Button>
            )
          : undefined,
      });
    },
    [addHit, removeHit, me, enqueueSnackbar, closeSnackbar],
  );

  const toggleTask = (id, index) => {
    const task = items.find((i) => i.id === id)?.blocker?.items?.[index];
    if (task?.id) board.setTask(id, task.id, !task.done);
  };

  /* drag and drop */

  const dragTimer = useRef(null);
  const onDragStart = (e, item) => {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", item.id);
    // Set the drag state on the next tick, not during dragstart. Starting a drag
    // re-renders the board (in Requests it reveals the Urgent drop box above the
    // cards), and changing the layout under the card while the browser is still
    // starting the drag makes it cancel the drag, so the press reads as a click.
    const drag = { id: item.id, from: item.column };
    clearTimeout(dragTimer.current);
    dragTimer.current = setTimeout(() => setDrag(drag), 0);
  };

  const onDragEnd = () => {
    clearTimeout(dragTimer.current);
    setDrag(null);
    setDropTarget(null);
  };

  /**
   * In ANOTHER lane the pointer position decides: in the slot between two
   * cards (or before the first / after the last) the drop line shows and a
   * release lands there; anywhere else the whole lane is the target and a
   * release adds the card to the top. In the card's own lane it's always
   * precise — the insertion point is the first card whose midpoint is below
   * the pointer — so reordering stays fluid.
   */
  const onDragOverColumn = (e, colKey, body) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    const dropTarget = dropRef.current;

    // Requests: the pointer picks the section (over the Urgent box → Urgent,
    // below it → Queue), then the card midpoints within that section.
    if (colKey === COL.REQUESTS && body) {
      const urgentEl = body.querySelector('[data-section="urgent"]');
      const section =
        urgentEl && e.clientY <= urgentEl.getBoundingClientRect().bottom ? "urgent" : "queue";
      const sectionEl = body.querySelector(`[data-section="${section}"]`) || body;
      const hit = [...sectionEl.querySelectorAll("[data-card-id]")]
        .filter((el) => el.dataset.cardId !== drag?.id)
        .find((el) => {
          const r = el.getBoundingClientRect();
          return e.clientY < r.top + r.height / 2;
        });
      const beforeId = hit ? hit.dataset.cardId : null;
      if (
        dropTarget?.col !== colKey ||
        dropTarget?.section !== section ||
        dropTarget?.beforeId !== beforeId
      ) {
        setDropTarget({ col: colKey, beforeId, mode: "precise", section });
      }
      return;
    }

    if (colKey !== drag?.from) {
      const slot = body ? slotAt(body, e.clientY, drag?.id) : undefined;
      const next =
        slot === undefined
          ? { col: colKey, beforeId: null, mode: "top" }
          : { col: colKey, beforeId: slot, mode: "precise" };
      if (
        dropTarget?.col !== next.col ||
        dropTarget?.beforeId !== next.beforeId ||
        dropTarget?.mode !== next.mode
      ) {
        setDropTarget(next);
      }
      return;
    }

    let beforeId = null;
    if (body) {
      const cards = [...body.querySelectorAll("[data-card-id]")].filter(
        (el) => el.dataset.cardId !== drag?.id,
      );
      const hit = cards.find((el) => {
        const r = el.getBoundingClientRect();
        return e.clientY < r.top + r.height / 2;
      });
      beforeId = hit ? hit.dataset.cardId : null;
    }
    if (
      dropTarget?.col !== colKey ||
      dropTarget?.beforeId !== beforeId ||
      dropTarget?.mode !== "precise"
    ) {
      setDropTarget({ col: colKey, beforeId, mode: "precise" });
    }
  };

  const onDropColumn = (e, colKey) => {
    e.preventDefault();
    const dropTarget = dropRef.current;
    if (drag) {
      // "top" mode: before the lane's first card (other than the one moving).
      const beforeId =
        dropTarget?.col === colKey && dropTarget.mode === "top"
          ? byColumn[colKey].find((i) => i.id !== drag.id)?.id ?? null
          : dropTarget?.beforeId ?? null;
      // Into Requests: the Urgent section marks it urgent; the Queue makes an
      // urgent card normal (a No rush card stays No rush).
      let urgency;
      if (colKey === COL.REQUESTS) {
        const current = items.find((i) => i.id === drag.id)?.urgency || "normal";
        urgency =
          dropTarget?.section === "urgent" ? "urgent" : current === "urgent" ? "normal" : current;
      }
      requestMove(drag.id, colKey, beforeId, urgency);
    }
    onDragEnd();
  };

  /* new request */

  /** Full form for a new request; `seed` carries a quick-add draft over. */
  const openNew = (column = COL.REQUESTS, seed = {}) =>
    setNewDraft({
      ...EMPTY_DRAFT,
      ...seed,
      // Quick-add images keep their File so they upload with the form.
      images: (seed.images || []).map((img) => ({
        id: img.id,
        file: img.file,
        name: img.name,
        file_display_name: img.name,
        url: img.url,
      })),
      mode: "new",
    });

  // Ctrl/Cmd+V while the New request form is open adds pasted screenshots to it.
  useEffect(() => {
    if (newDraft?.mode !== "new") return undefined;
    const onPaste = (e) => {
      const files = pastedImageFiles(e);
      if (!files.length) return;
      e.preventDefault();
      setNewDraft((prev) =>
        prev
          ? {
              ...prev,
              images: [
                ...(prev.images || []),
                ...filesToImages(files),
              ],
            }
          : prev,
      );
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [newDraft?.mode]);

  /** The same form, filled from an existing item. */
  const openEdit = (item) =>
    setNewDraft({
      mode: "edit",
      id: item.id,
      needs: item.needs || [],
      urgency: item.urgency || "normal",
      title: item.title,
      description: item.description || "",
      platform: item.platform || "both",
      context: item.context || null,
    });

  const quickAdd = (column, draft) =>
    board.createItem(
      {
        needs: draft.needs,
        urgency: draft.urgency,
        title: draft.title.trim(),
        description: draft.description.trim(),
        platform: draft.platform || "both",
        context: null,
        area: "",
        images: draft.images || [],
      },
      draft.position || "bottom",
    );

  const saveNew = async () => {
    setSaving(true);
    if (newDraft.mode === "edit") {
      const before = items.find((i) => i.id === newDraft.id);
      const next = {
        needs: newDraft.needs || [],
        urgency: newDraft.urgency || "normal",
        title: newDraft.title.trim(),
        description: newDraft.description.trim(),
        platform: newDraft.platform || "both",
        context: newDraft.context || null,
      };
      // The area line follows the link; cards without one keep theirs.
      next.area = next.context ? contextArea(next.context) : before?.area || "";
      // Send only what changed (the server records exactly those fields).
      const patch = Object.fromEntries(
        Object.entries(next).filter(([k, v]) => !sameValue(k, before?.[k], v)),
      );
      const ok = !Object.keys(patch).length || (await board.updateItem(newDraft.id, patch));
      setSaving(false);
      if (ok) setNewDraft(null);
      return;
    }
    const ok = await board.createItem(
      {
        needs: newDraft.needs || [],
        urgency: newDraft.urgency || "normal",
        title: newDraft.title.trim(),
        description: newDraft.description.trim(),
        platform: newDraft.platform || "both",
        context: newDraft.context || null,
        area: contextArea(newDraft.context),
        images: newDraft.images || [],
      },
      newDraft.position,
    );
    setSaving(false);
    if (ok) setNewDraft(null);
  };

  if (!isSuperAdmin) return <Navigate to="/support" replace />;

  const pendingItem = pending ? items.find((i) => i.id === pending.id) : null;

  return (
    // The item panel is a fixed drawer over the board's right edge (no
    // backdrop), so the board stays usable while it's open.
    <Flx sx={{ height: "100%", minHeight: 0 }}>
    <Box sx={{ flex: 1, minWidth: 0, height: "100%" }}>
    <AdminScreenShell
      maxWidth="none"
      background={A.surface}
      fillHeight
      // 24px — the same side inset as the app header; the board scroller below
      // cancels it to run flush to the edges and re-pads its lanes to match.
      padding={3}
      title="Request Board"
      heading={
        <Flx
          role="tablist"
          aria-label="Request Board views"
          sx={{ gap: 2.5, alignItems: "flex-end" }}
        >
          <TitleTab
            active={view === "board"}
            label="Request Board"
            onClick={() => setView("board")}
          />
          <TitleTab
            active={view === "history"}
            label="History"
            onClick={() => setView("history")}
          />
        </Flx>
      }
      subtitleMaxWidth="none"
      subtitle="Requests are worked top to bottom, urgent ones first; finished work comes back for review. Drag a card, or open it to comment and move it."
      actions={
        <>
          <Tooltip title="Refresh">
            <span>
              <IconButton
                aria-label="Refresh the board"
                onClick={board.refresh}
                disabled={board.refreshing || board.loading}
                sx={{
                  width: HEADER_BTN.height,
                  height: HEADER_BTN.height,
                  borderRadius: HEADER_BTN.borderRadius,
                  border: `1px solid ${A.lineStrong}`,
                  bgcolor: A.surface,
                  color: neutralButton.text,
                  boxShadow: A.shadowSm,
                  "&:hover": { bgcolor: slate[50], borderColor: A.muted },
                  "& svg": {
                    fontSize: 19,
                    animation: board.refreshing ? "rbSpin .8s linear infinite" : "none",
                  },
                  "@keyframes rbSpin": { to: { transform: "rotate(360deg)" } },
                }}
              >
                <RefreshRounded />
              </IconButton>
            </span>
          </Tooltip>
          {/* The board developers' sign-in token, for the Claude Code board commands. */}
          {canSeeRequestBoardToken ? (
            <Tooltip title="Auth token">
              <IconButton
                aria-label="Show my auth token"
                onClick={() => setTokenOpen(true)}
                sx={{
                  width: HEADER_BTN.height,
                  height: HEADER_BTN.height,
                  borderRadius: HEADER_BTN.borderRadius,
                  border: `1px solid ${A.lineStrong}`,
                  bgcolor: A.surface,
                  color: neutralButton.text,
                  boxShadow: A.shadowSm,
                  "&:hover": { bgcolor: slate[50], borderColor: A.muted },
                  "& svg": { fontSize: 19 },
                }}
              >
                <KeyRounded />
              </IconButton>
            </Tooltip>
          ) : null}
          {/* White, hairline-bordered, gray-700 label and icon — a quiet
              secondary action (background and text color are set together). */}
          <Button
            variant="outlined"
            startIcon={<NotificationsNoneOutlined />}
            onClick={() => setAlertsOpen(true)}
            sx={{
              ...HEADER_BTN,
              bgcolor: A.surface,
              color: neutralButton.text,
              borderColor: A.lineStrong,
              boxShadow: A.shadowSm,
              "& .MuiButton-startIcon": { color: neutralButton.text },
              "&:hover": { bgcolor: slate[50], borderColor: A.muted },
            }}
          >
            Alerts
          </Button>
          {/* Both sides can file requests. */}
          <Button
            variant="contained"
            startIcon={<AddRounded />}
            onClick={() => openNew()}
            sx={HEADER_BTN}
          >
            New request
          </Button>
        </>
      }
    >
      {board.loading ? (
        <Flx center sx={{ flex: 1, minHeight: 300 }}>
          <CircularProgress size={28} />
        </Flx>
      ) : board.loadError ? (
        <Flx column center g={1.5} sx={{ flex: 1, minHeight: 300 }}>
          <Txt sx={{ fontSize: 14, color: A.ink2 }}>{board.loadError}</Txt>
          <Button variant="outlined" color="gray" onClick={board.reload}>
            Try again
          </Button>
        </Flx>
      ) : view === "history" ? (
        <HistoryPanel
          events={boardHistory}
          itemExists={(id) => items.some((i) => i.id === id)}
          onOpenItem={(id) => {
            setView("board");
            setOpenId(id);
          }}
        />
      ) : (
      <>
      {/* Five lanes in three zones (requests | development | review); scrolls
          sideways on narrow windows rather than wrapping. Full-bleed scroller:
          it cancels the shell's side / bottom inset so the board and its
          scrollbar run flush to the window edges, then pads its content so the
          lanes line up with the header (16.5px + the zone's 1.5px border + 6px
          padding = the header's 24px). */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowX: "auto",
          overflowY: "hidden",
          mx: -3,
          mb: -3,
          px: "16.5px",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: ZONE_TEMPLATE,
            gap: 1.5,
            height: "100%",
            minHeight: 400,
          }}
        >
          {ZONES.map((zone) => (
            <BoardZone key={zone.key} zone={zone}>
              {COLUMNS.filter((c) => c.zone === zone.key).map((col) => (
                <BoardColumn
                  key={col.key}
                  col={col}
                  tinted={!!zone.tinted}
                  items={byColumn[col.key]}
                  nextUpId={nextUpId}
                  drag={drag}
                  dropTarget={dropTarget}
                  onAdd={openNew}
                  onQuickAdd={quickAdd}
                  onOpenImages={openImages}
                  onOpen={setOpenId}
                  openId={openId}
                  onDragStart={onDragStart}
                  onDragEnd={onDragEnd}
                  onDragOverColumn={onDragOverColumn}
                  onDropColumn={onDropColumn}
                  me={me}
                  onHit={hit}
                  onBulk={setBulkFrom}
                />
              ))}
            </BoardZone>
          ))}
        </Box>
      </Box>
      </>
      )}
    </AdminScreenShell>
    </Box>

      <RequestBoardItemDrawer
        item={openItem}
        me={me}
        onClose={() => setOpenId(null)}
        onMove={(id, to) => requestMove(id, to)}
        onComment={(id, body, images) => board.addComment(id, body, images)}
        onAddImages={(id, imgs) => board.addImages(id, imgs)}
        onRemoveImage={(id, imgId) => board.removeImage(id, imgId)}
        history={itemHistory}
        onOpenImages={openImages}
        onDelete={setDeleteId}
        onEdit={openEdit}
        lane={openItem ? byColumn[openItem.column] : []}
        onNavigate={setOpenId}
        onReorder={(id, beforeId) => {
          const it = items.find((i) => i.id === id);
          if (it) board.moveItem(id, it.column, beforeId);
        }}
        onUpdate={(id, key, value) => board.updateItem(id, { [key]: value })}
        onEditHandoff={(id) => setHandoff({ id, beforeId: null, edit: true })}
        onAnswer={(id, answer) => board.answerItem(id, answer)}
        onToggleTask={toggleTask}
        onHit={hit}
      />

      <HandoffDialog
        open={!!handoffItem}
        item={handoffItem}
        blocker={handoff?.edit ? handoffItem?.blocker || null : null}
        onCancel={() => setHandoff(null)}
        onSubmit={submitHandoff}
        onOpenImages={openImages}
      />

      {/* Moves that need words first. A form in a modal follows the "Add RTL
          tier" pattern (CLAUDE.md "Form field labels & helpers"):
          QuickEditDialog + label-above FormPrimitives on a FormFieldGrid. */}
      <QuickEditDialog
        open={!!pending}
        title="Send back"
        description={pendingItem?.title}
        saveText="Send back"
        canSave={!!pending?.text.trim() && !saving}
        onSave={confirmPending}
        onClose={() => setPending(null)}
        hideFieldLabel={false}
        enterHint={false}
        bodySx={{ pt: px(FORM.s.s5) }}
        fieldText="field"
      >
        {pending ? (
          <FormFieldGrid>
            <FormTextField
              span={6}
              label="What still needs to change"
              required
              multiline
              minRows={3}
              value={pending.text}
              onChange={(v) => setPending((prev) => ({ ...prev, text: v }))}
              helperText="Posted as a comment on the item."
            />
            <ImagesField
              images={pending.images || []}
              onOpenImages={openImages}
              helperText="Paste screenshots (Ctrl+V) anywhere in this form, drop them here, or click the tile. They're posted with the comment."
              onChange={(images) => setPending((prev) => ({ ...prev, images }))}
            />
          </FormFieldGrid>
        ) : null}
      </QuickEditDialog>

      <QuickEditDialog
        open={!!newDraft}
        title={newDraft?.mode === "edit" ? "Edit request" : "New request"}
        description={
          newDraft?.mode === "edit"
            ? "Change what the request says. Move it between columns from the board."
            : "Describe it in your own words — everything else is optional."
        }
        status={
          newDraft?.mode === "new" ? (
            <Flx ac g={1}>
              <Box
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  flexShrink: 0,
                  bgcolor: newDraft.urgency === "urgent" ? status.error.fg : teal[500],
                }}
              />
              <span>
                {newDraft.urgency === "urgent"
                  ? newDraft.position === "top"
                    ? "Pins to the top of Urgent"
                    : "Pins to Urgent at the top of Requests"
                  : newDraft.position === "top"
                    ? "Adds to the top of the queue"
                    : "Adds to the bottom of the queue"}
              </span>
            </Flx>
          ) : null
        }
        saveText={newDraft?.mode === "edit" ? "Save changes" : "Add request"}
        canSave={!!newDraft?.title.trim() && !saving}
        onSave={saveNew}
        onClose={() => setNewDraft(null)}
        hideFieldLabel={false}
        width={580}
        fieldText="field"
      >
        {newDraft ? (
          // Ctrl/Cmd+Enter saves from anywhere in the form (Enter alone does
          // from a one-line field).
          <Box
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.ctrlKey || e.metaKey) && newDraft.title.trim() && !saving) {
                e.preventDefault();
                saveNew();
              }
            }}
          >
            <FormFieldGrid>
              <FormTextField
                span={6}
                label="Request"
                placeholder="e.g. Default to one unit for SFR on the DSCR tab"
                value={newDraft.title}
                onChange={(v) => setNewDraft((prev) => ({ ...prev, title: v }))}
              />
              <FormTextField
                span={6}
                label={<OptionalLabel>Details</OptionalLabel>}
                multiline
                minRows={3}
                placeholder="What happened or what you need, and an example loan if there is one."
                value={newDraft.description}
                onChange={(v) => setNewDraft((prev) => ({ ...prev, description: v }))}
              />
              <FormField
                span={6}
                label={<OptionalLabel hint="pick any, or leave blank">What do you need?</OptionalLabel>}
              >
                <Box
                  role="group"
                  aria-label="What do you need?"
                  sx={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 1.25 }}
                >
                  {NEED_CARDS.map((n) => {
                    const on = (newDraft.needs || []).includes(n.key);
                    return (
                      <NeedCard
                        key={n.key}
                        need={n}
                        on={on}
                        onToggle={() =>
                          setNewDraft((prev) => ({
                            ...prev,
                            needs: on
                              ? (prev.needs || []).filter((k) => k !== n.key)
                              : [...(prev.needs || []), n.key],
                          }))
                        }
                      />
                    );
                  })}
                </Box>
              </FormField>
              {/* One box: search loans or screens by name, or paste a link to a
                  loan, an exact drilldown tab, or any screen. */}
              <Box sx={{ gridColumn: "1 / -1" }}>
                <FormLinkTargetField
                  label="Loan or area this relates to"
                  hint="optional"
                  placeholder="Loan #, address, screen, or paste a link"
                  screens={APP_SCREENS}
                  tabsFor={tabsFor}
                  value={newDraft.context}
                  onChange={(context) => setNewDraft((prev) => ({ ...prev, context }))}
                />
              </Box>
              <FormField span={2} label="How soon?">
                <FormSelect
                  label="How soon?"
                  value={newDraft.urgency || "normal"}
                  options={URGENCY.map((u) => ({ value: u.key, label: u.label }))}
                  onChange={(v) => setNewDraft((prev) => ({ ...prev, urgency: v }))}
                  icon={
                    <ScheduleRounded
                      sx={{
                        fontSize: 18,
                        color: newDraft.urgency === "urgent" ? status.error.fg : teal[600],
                      }}
                    />
                  }
                />
              </FormField>
              <FormField span={2} label="Company">
                <FormSelect
                  label="Company"
                  value={newDraft.platform || "both"}
                  options={PLATFORMS.map((p) => ({ value: p.value, label: p.label }))}
                  onChange={(v) => setNewDraft((prev) => ({ ...prev, platform: v }))}
                />
              </FormField>
              {newDraft.mode === "edit" ? null : (
                <FormField span={2} label="Add to">
                  <FormSelect
                    label="Add to"
                    value={newDraft.position || "bottom"}
                    options={
                      newDraft.urgency === "urgent"
                        ? [
                            { value: "top", label: "Top of Urgent" },
                            { value: "bottom", label: "Bottom of Urgent" },
                          ]
                        : [
                            { value: "top", label: "Top of queue" },
                            { value: "bottom", label: "Bottom of queue" },
                          ]
                    }
                    onChange={(v) => setNewDraft((prev) => ({ ...prev, position: v }))}
                  />
                </FormField>
              )}
              {newDraft.mode === "edit" ? null : (
                <ImagesField
                  images={newDraft.images}
                  onOpenImages={openImages}
                  helperText="Paste screenshots (Ctrl+V) anywhere in this form, drop them here, or click the tile."
                  onChange={(images) => setNewDraft((prev) => ({ ...prev, images }))}
                />
              )}
            </FormFieldGrid>
          </Box>
        ) : null}
      </QuickEditDialog>

      <ImageViewer viewer={viewer} onClose={() => setViewer(null)} />

      <AlertsDialog
        open={alertsOpen}
        prefs={alertPrefs}
        onClose={() => setAlertsOpen(false)}
        onSave={async (next) => {
          try {
            setAlertPrefs(await requestBoardApi.saveAlertPrefs(next));
            setAlertsOpen(false);
            enqueueSnackbar("Alert settings saved", { variant: "success" });
          } catch (err) {
            enqueueSnackbar(requestBoardErrorMessage(err, "Alert settings couldn't be saved."), {
              variant: "error",
            });
          }
        }}
        onTestEmail={async () => {
          try {
            const res = await requestBoardApi.sendTestEmail();
            enqueueSnackbar(`Test email sent to ${res.to}`, { variant: "success" });
          } catch (err) {
            enqueueSnackbar(requestBoardErrorMessage(err, "The test email couldn't be sent."), {
              variant: "error",
            });
          }
        }}
      />

      <AuthTokenDialog open={tokenOpen} onClose={() => setTokenOpen(false)} />

      <ConfirmationDialog
        open={!!bulkFrom}
        title={bulkFrom ? `${BULK_MOVES[bulkFrom].label}?` : ""}
        message={
          bulkFrom
            ? `${(byColumn[bulkFrom] || []).length} card${(byColumn[bulkFrom] || []).length === 1 ? "" : "s"} will move from ${columnByKey(bulkFrom)?.label} to ${columnByKey(BULK_MOVES[bulkFrom].to)?.label}.`
            : ""
        }
        confirmText={bulkFrom ? BULK_MOVES[bulkFrom].label : "Move"}
        onConfirm={async () => {
          const from = bulkFrom;
          setBulkFrom(null);
          const ids = (byColumn[from] || []).map((i) => i.id);
          const { to } = BULK_MOVES[from];
          const moved = await board.moveMany(ids, to, from);
          if (moved != null) {
            enqueueSnackbar(
              `Moved ${moved} card${moved === 1 ? "" : "s"} to ${columnByKey(to)?.label}`,
              { variant: "success" },
            );
          }
        }}
        onCancel={() => setBulkFrom(null)}
      />

      <ConfirmationDialog
        open={!!deleteId}
        title="Delete request?"
        message="It will be removed from the board."
        confirmText="Delete"
        destructive
        onConfirm={async () => {
          const id = deleteId;
          setDeleteId(null);
          if (await board.deleteItem(id)) setOpenId(null);
        }}
        onCancel={() => setDeleteId(null)}
      />
    </Flx>
  );
}
